import { useRef, useState } from 'react';
import { Loader2, MicVocal, Pause, Play, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useTtsAccess } from '@/features/tts-flow/api';
import { useUserLanguages } from '@/features/voice-campaigns/api';
import { useAuthStore } from '@/lib/auth/store';
import { cn } from '@/lib/utils/cn';
import {
  useConvertTextToSpeech,
  useDiscardTts,
  useFinalizeTts,
} from '../api';
import { findBlockedWords, useBlockedWordsCheck } from '@/features/blocked-words/api';
import {
  CATEGORY_LABEL,
  PROMPT_CATEGORIES,
  type PromptCategory,
  type TtsGender,
} from '../types';

interface Props {
  onSuccess?: () => void;
}

const MAX_CHARS = 1000;

interface PreviewState {
  ttsId: number;
  downloadUrl: string;
  promptDuration: number;
  characterCount: number;
}

export function SynthesizePanel({ onSuccess }: Props) {
  const userId = useAuthStore((s) => s.userId);
  const ttsAccess = useTtsAccess();
  const languages = useUserLanguages();

  const convert = useConvertTextToSpeech();
  const finalize = useFinalizeTts();
  const discard = useDiscardTts();
  const blockedWordsCheck = useBlockedWordsCheck();

  const [language, setLanguage] = useState('');
  const [gender, setGender] = useState<TtsGender | ''>('');
  const [category, setCategory] = useState<PromptCategory | ''>('');
  const [fileName, setFileName] = useState('');
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<PreviewState | null>(null);

  /* ---------------- Audio playback ---------------- */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  const stopAudio = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(false);
  };

  const toggleAudio = () => {
    if (!preview) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(preview.downloadUrl);
      audioRef.current.addEventListener('ended', () => setPlaying(false));
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      void audioRef.current.play();
      setPlaying(true);
    }
  };

  /* ---------------- Actions ---------------- */
  const handlePreview = (e: React.FormEvent) => {
    e.preventDefault();
    if (ttsAccess.blocked) return toast.error(ttsAccess.message ?? 'TTS is not available.');
    if (!text.trim()) return toast.error('Write the text to synthesize.');
    if (!language) return toast.error('Pick a language.');
    if (!gender) return toast.error('Pick a voice.');
    if (!category) return toast.error('Pick a category.');
    if (!fileName.trim()) return toast.error('Name the prompt.');

    stopAudio();

    // ── Blocked word check ──
    const blocked = findBlockedWords(text, blockedWordsCheck.data?.words ?? []);
    if (blocked.length > 0) {
      toast.error(
        `Your text contains blocked word${blocked.length > 1 ? 's' : ''}: "${blocked.join('", "')}". Remove ${blocked.length > 1 ? 'them' : 'it'} to synthesize.`,
      );
      return;
    }

    convert.mutate(
      {
        userId: Number(userId),
        gender,
        textScript: text,
        characterCount: text.length,
        language,
        filename: fileName.trim(),
        category,
      },
      {
        onSuccess: (res) => {
          if (res.status === 'fail') {
            toast.error(res.msg ?? 'Could not synthesize.');
            return;
          }
          setPreview({
            ttsId: res.ttsId,
            downloadUrl: res.downloadUrl,
            promptDuration: res.promptDuration,
            characterCount: res.characterCount,
          });
          toast.success('Preview ready — listen and save to the library.');
        },
        onError: () => toast.error('Could not synthesize.'),
      },
    );
  };

  const handleSave = () => {
    if (!preview) return;
    if (!category || !gender) return;

    finalize.mutate(
      {
        id: preview.ttsId,
        userId: Number(userId),
        status: 1,
        dowmloadUrl: preview.downloadUrl,
        promptName: fileName.trim(),
        promptDuration: String(preview.promptDuration),
        characterCount: preview.characterCount,
        category,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Saved to library');
          stopAudio();
          setPreview(null);
          setText('');
          setFileName('');
          onSuccess?.();
        },
        onError: () => toast.error('Could not save the prompt.'),
      },
    );
  };

  const handleDiscard = () => {
    if (!preview) return;
    discard.mutate(preview.ttsId, {
      onSuccess: () => {
        stopAudio();
        setPreview(null);
        toast.success('Preview discarded');
      },
      onError: () => toast.error('Could not discard preview.'),
    });
  };

  const overChar = text.length > MAX_CHARS;

  return (
    <form className="space-y-4" onSubmit={handlePreview}>

      {/* Hero textarea */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="tts-text">
            What should the bot say?
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <span
            className={cn(
              'text-[11px] font-mono',
              overChar ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {text.length} / {MAX_CHARS}
          </span>
        </div>
        <Textarea
          id="tts-text"
          rows={5}
          maxLength={MAX_CHARS + 20}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Hi, thanks for calling. Press 1 to speak to a human, press 2 for sales…"
          className="resize-none"
        />
      </div>

      {/* Voice + category row */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>
            Language
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger>
              <SelectValue
                placeholder={
                  languages.isLoading
                    ? 'Loading…'
                    : (languages.data ?? []).length === 0
                      ? 'No languages enabled'
                      : 'Pick a language'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {(languages.data ?? []).map((l, i) => (
                    <SelectItem key={`${l.language}-${i}`} value={l.languageCode ?? l.language}>
                  {l.language}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>
            Voice
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <Select
            value={gender}
            onValueChange={(v) => setGender(v as TtsGender)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Pick a voice" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MALE">Male</SelectItem>
              <SelectItem value="FEMALE">Female</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tts-name">
            Prompt name
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <Input
            id="tts-name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="e.g. welcome-en-male"
          />
        </div>
        <div className="space-y-1.5">
          <Label>
            Category
            <span className="ml-1 text-destructive">*</span>
          </Label>
          <Select
            value={category}
            onValueChange={(v) => setCategory(v as PromptCategory)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Pick a category" />
            </SelectTrigger>
            <SelectContent>
              {PROMPT_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {CATEGORY_LABEL[c]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Preview player — appears after a successful convert. */}
      {preview && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-emerald-900">
            <MicVocal className="h-3.5 w-3.5" />
            Preview ready — {preview.characterCount} chars ·{' '}
            {Number(preview.promptDuration).toFixed(1)}s
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full"
              onClick={toggleAudio}
              aria-label={playing ? 'Pause preview' : 'Play preview'}
            >
              {playing ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>
            <div className="flex-1 text-xs text-muted-foreground">
              Listening before save uses no extra credits.
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDiscard}
              disabled={discard.isPending}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Discard
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={finalize.isPending}
            >
              {finalize.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save to library
            </Button>
          </div>
        </div>
      )}

      {/* Synthesize button — hidden once preview exists; user must Save or
          Discard first to keep the flow linear. */}
      {!preview && (
        <div className="flex items-center justify-end gap-2">
          <Button
            type="submit"
            disabled={convert.isPending || overChar}
          >
            {convert.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MicVocal className="h-4 w-4" />
            )}
            Synthesize & preview
          </Button>
        </div>
      )}
    </form>
  );
}
